import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, ShieldX, RotateCcw, Filter } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "direction", "administration", "finance", "coordinateur", "professeur", "parent", "eleve"];
const STCOLOR = { active: "bg-emerald-100 text-emerald-700", suspended: "bg-red-100 text-red-700", archived: "bg-slate-100 text-slate-500", pending: "bg-amber-100 text-amber-700" };

const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return "—"; } };

export default function SecurityAdmin() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [onlyUnconfigured, setOnlyUnconfigured] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isSuperAdmin = hasRole("super_admin");
  const canReset = hasRole("super_admin", "direction");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/security/users"); setRows(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (roleFilter !== "all" && !(r.roles || []).includes(roleFilter)) return false;
    if (onlyUnconfigured && !r.reset_required) return false;
    return true;
  }), [rows, roleFilter, onlyUnconfigured]);

  const unconfiguredCount = rows.filter((r) => r.reset_required).length;

  const openReset = (row) => { setTarget(row); setReason(""); };
  const canResetRow = (row) => canReset && !((row.roles || []).includes("super_admin") && !isSuperAdmin);

  const confirmReset = async () => {
    if (!reason.trim()) { toast.error("Un motif est obligatoire."); return; }
    setBusy(true);
    try {
      await api.post("/auth/mfa/reset", { user_id: target.id, reason: reason.trim() });
      toast.success("Double authentification réinitialisée.");
      setTarget(null); setReason(""); await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="security-page">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-display font-bold text-slate-900">Sécurité — Double authentification</h1>
        </div>
        <a href="/audit?module=security" data-testid="security-audit-link" className="text-sm text-primary hover:underline">{t("viewHistory")}</a>
      </div>
      <p className="text-sm text-slate-500 mb-6">Pilotage MFA avant publication. Aucun secret, code de récupération ou mot de passe n'est affiché.</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-52" data-testid="filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`role_${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="filter-unconfigured" variant={onlyUnconfigured ? "default" : "outline"} size="sm"
          onClick={() => setOnlyUnconfigured((v) => !v)} className="gap-2 rounded-full">
          <ShieldAlert className="h-4 w-4" /> MFA obligatoire non configuré
          <Badge variant="secondary" className="ml-1">{unconfiguredCount}</Badge>
        </Button>
      </div>

      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("email")}</TableHead>
              <TableHead>{t("roles")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>MFA requis</TableHead>
              <TableHead>MFA activé</TableHead>
              <TableHead>Activée le</TableHead>
              <TableHead>Dernier usage</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Chargement…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Aucun utilisateur.</TableCell></TableRow>
            ) : filtered.map((u, i) => (
              <TableRow key={u.id} data-testid="security-row" className={i % 2 ? "bg-slate-50/40" : ""}>
                <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                <TableCell className="text-slate-600">{u.email_masked}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(u.roles || []).map((r) => <Badge key={r} variant="secondary" className="text-[10px]">{t(`role_${r}`)}</Badge>)}</div></TableCell>
                <TableCell><Badge className={`${STCOLOR[u.status] || STCOLOR.active} border-0`}>{t(`ust_${u.status}`) !== `ust_${u.status}` ? t(`ust_${u.status}`) : u.status}</Badge></TableCell>
                <TableCell>{u.mfa_required ? <Badge className="bg-amber-100 text-amber-700 border-0">Oui</Badge> : <span className="text-slate-400 text-sm">Non</span>}</TableCell>
                <TableCell>
                  {u.mfa_enabled
                    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-sm" data-testid="mfa-on"><ShieldCheck className="h-4 w-4" />Activée</span>
                    : u.reset_required
                      ? <span className="inline-flex items-center gap-1 text-red-600 text-sm" data-testid="mfa-required-off"><ShieldX className="h-4 w-4" />À configurer</span>
                      : <span className="text-slate-400 text-sm">—</span>}
                </TableCell>
                <TableCell className="text-slate-600 text-sm">{fmt(u.mfa_enrolled_at)}</TableCell>
                <TableCell className="text-slate-600 text-sm">{fmt(u.mfa_last_used_at)}</TableCell>
                <TableCell className="text-right">
                  {u.mfa_enabled && (
                    <Button data-testid={`mfa-reset-btn-${u.id}`} variant="ghost" size="sm" className="gap-1 text-red-600"
                      disabled={!canResetRow(u)} onClick={() => openReset(u)}>
                      <RotateCcw className="h-4 w-4" /> Réinitialiser MFA
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-md" data-testid="reset-dialog">
          <DialogHeader><DialogTitle className="font-display">Réinitialiser la double authentification</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-slate-600">L'utilisateur <strong>{target.name}</strong> devra reconfigurer sa 2FA à sa prochaine connexion. Cette action est journalisée.</p>
              <div className="grid gap-1.5">
                <label className="text-sm font-medium text-slate-600">Motif (obligatoire)</label>
                <Textarea data-testid="reset-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                  placeholder="Ex. : téléphone perdu, changement d'appareil…" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="reset-cancel" variant="outline" onClick={() => setTarget(null)}>{t("cancel")}</Button>
            <Button data-testid="reset-confirm" className="text-white" disabled={busy || !reason.trim()} onClick={confirmReset}>
              {busy ? "…" : "Confirmer la réinitialisation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
